import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  startWith,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { HasPermissionDirective, triggerIfSlow, UserService, WebSocketService } from '@ipg/ngx-frontend-toolset';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DialogService } from '../../components/dialog/dialog.service';
import { Client } from '../../models/clients.model';

import { MatFormField, MatInput, MatPrefix, MatSuffix } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { truncate } from 'lodash-es';
import { escapeHtml } from '../../utils/html';
import { HeaderComponent } from '../../components/header/header.component';

import { GlobalLoadersService } from '../../global-loaders.service';
import { User } from '../../models/user.model';
import { TableLimitService } from '../../services/table-limit.service';
import {
  CreateClientDialogComponent,
  CreateClientDialogResult,
} from './components/create-client-dialog/create-client-dialog.component';
import { ClientsService } from './services/clients.service';

@Component({
  selector: 'app-clients',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTableModule,
    MatTooltip,
    MatPaginator,
    MatSort,
    MatSortHeader,
    DatePipe,
    DataTableContainerComponent,
    MatInput,
    MatFormField,
    MatPrefix,
    MatSuffix,
    FormsModule,
    HasPermissionDirective,
  ],
  templateUrl: './clients.component.html',
  styleUrl: './clients.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientsComponent {
  private static readonly LOADER_CALLER_ID = 'clients_loader_caller';

  private readonly _clients = inject(ClientsService);
  private readonly _userService = inject<UserService<User>>(UserService);
  private readonly _tableLimit = inject(TableLimitService);
  private readonly _dialog = inject(DialogService);
  private readonly _snackbar = inject(MatSnackBar);

  private readonly _wsService = inject(WebSocketService);

  private readonly _fetch$ = new Subject<void>();

  private readonly _keywordInput$ = new Subject<string | null>();
  private readonly _debouncedKeyword$ = this._keywordInput$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this._query.update((currentState) => ({ ...currentState, page: 1 })))
  );

  protected readonly keyword = signal<string | null>(null);

  private readonly _hasLoadedData = signal<boolean>(false);

  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _sort = signal({
    sortBy: 'name',
    sortOrder: 'asc',
  });

  /** Emits whenever the active unit changes, triggering a data re-fetch. */
  private readonly _unitId$ = toObservable(
    computed(() => this._userService.data()?.unitId)
  ).pipe(distinctUntilChanged());

  private readonly _params$ = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    keyword: this._debouncedKeyword$.pipe(startWith(this.keyword())),
    __fetch: this._fetch$.pipe(startWith(undefined)),
    __unitId: this._unitId$,
  }).pipe(
    map(({ query, sort, keyword }) => ({
      ...query,
      ...sort,
      ...(keyword ? { q: keyword.trim() } : null),
    })),
    debounceTime(0)
  );

  private readonly _paginatedClients$ = this._params$.pipe(
    switchMap((params) =>
      this._clients
        .getAll({
          params,
        })
        .pipe(
          tap({
            error: () =>
              this._dialog.alert({
                title: `Foutmelding`,
                message: `Er is iets misgegaan bij het ophalen van de cliënten. Probeer het later nog eens`,
                confirmText: `OK`,
              }),
          }),
          takeUntilDestroyed(this._destroyRef),
          triggerIfSlow(() =>
            this._globalLoaders.setActive(
              ClientsComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          ),
          finalize(() =>
            this._globalLoaders.setInactive(
              ClientsComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          )
        )
    ),
    tap(() => this._hasLoadedData.set(true)),
    tap(({ meta }) => this._setPaginator({ page: meta.page, total: meta.total }))
  );

  private readonly _clients$ = this._paginatedClients$.pipe(map(({ items }) => items));

  private readonly _paginator = viewChild.required(MatPaginator);

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _globalLoaders = inject(GlobalLoadersService);

  protected readonly dataSource = toSignal(this._clients$, { initialValue: [] });

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly isSearchDisabled = computed(() => !this._hasLoadedData());

  protected readonly router = inject(Router);

  constructor() {
    this._wsService
      .connect('/event-stream', {
        requireAuth: true,
        topics: ['unit/+/client/changed'],
      })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (connection) => {
          connection.events$.subscribe(() => this._fetch$.next());
        },
      });
  }

  protected readonly displayedColumns = [
    'idAlias',
    'name',
    'createdAt',
    'updatedAt',
    'actions',
  ];

  addClient() {
    this._dialog.open(
      CreateClientDialogComponent,
      {
        onConfirm: (result?: CreateClientDialogResult) =>
          this._clients.create(result).pipe(
            tap(() => this._fetch$.next()),
            catchError((error) => {
              this._dialog.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het aanmaken van een cliënt. Probeer het later nog eens',
                confirmText: 'OK',
              });

              return throwError(() => error);
            })
          ),
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  deleteClient(client: Client) {
    this._dialog.confirm({
      title: `${client.name} verwijderen?`,
      message:
        'De cliënt wordt gepland voor verwijdering en kan binnen 10 dagen hersteld worden. ' +
        'Na 10 dagen worden apparaten losgekoppeld en terug in de voorraad geplaatst, en worden (geplande) interacties en de signaleringsmodule permanent verwijderd. ' +
        'Weet je zeker dat je de cliënt wilt verwijderen?',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () =>
        this._clients.delete(client._id).pipe(
          tap(() => {
            this._snackbar.open(
              `Cliënt ${truncate(client.name)} is gepland voor verwijdering`,
              'OK'
            );
            this._fetch$.next();
          }),
          catchError((error) => {
            this._dialog.alert({
              title: 'Foutmelding',
              message: `Er is iets misgegaan bij het verwijderen van cliënt ${escapeHtml(client.name)}. Probeer het later nog eens`,
              confirmText: 'OK',
            });

            return throwError(() => error);
          })
        ),
    });
  }

  restoreClient(client: Client) {
    this._clients
      .restore(client._id)
      .pipe(
        tap(() => {
          this._snackbar.open(
            `Cliënt ${truncate(client.name)} is succesvol hersteld`,
            'OK'
          );
          this._fetch$.next();
        }),
        catchError((error) => {
          this._dialog.alert({
            title: 'Foutmelding',
            message: `Er is iets misgegaan bij het herstellen van cliënt ${escapeHtml(client.name)}. Probeer het later nog eens`,
            confirmText: 'OK',
          });

          return throwError(() => error);
        })
      )
      .subscribe();
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim());
    this._keywordInput$.next(value.trim());
  }

  clearSearch() {
    this.keyword.set(null);
    this._keywordInput$.next(null);
  }

  onRowDblClick(client: Client) {
    if (client.deleteAt != null || client.moveId != null) return;
    this.router.navigate(['clients', client._id]);
  }

  moveClient(client: Client) {
    this.router.navigate(['clients', client._id, 'move']);
  }

  private _setPaginator({ page, total }: { page: number; total: number }) {
    this._paginator().pageIndex = page - 1;
    this._paginator().length = total;
  }
}
